import { ref } from "vue"
import Swal from "sweetalert2"
import { useAuth } from "@/composables/useAuth"
import api from "@/api/axios"
import { esperarJob } from "@/composables/useJobPolling"

export function useVuelos(opciones = {}) {

    // notificar: alerta contextual (por defecto Swal global, para compatibilidad)
    const notificar = opciones.notificar || ((opts) => Swal.fire(opts))

    // onJob: reporta el job activo ({ tipo, jobId }) o null al terminar
    const onJob = opciones.onJob || (() => {})

    const resultados = ref({
        vuelos: [],
        precio: null,
        resumen: "",
    })
    const loading = ref(false)
    const searchId = ref(null)

    async function procesarBusqueda(jobId) {

        onJob({ tipo: "buscar", jobId })

        const res = await esperarJob(jobId)

        resultados.value = {
            vuelos: res.resultados || [],
            precio: res.precio || null,
            resumen: res.resumen || "",
        }
        searchId.value = res.searchId

        return res
    }

    // =========================
    // ✈️ BUSCAR VUELOS
    // =========================
    async function buscarVuelos(params) {
        loading.value = true
        resultados.value = {
            vuelos: [],
            precio: null,
            resumen: "",
        }

        try {
            const { data } = await api.post("/buscar-vuelos", params)

            if (!data.ok) throw new Error(data.error)

            return await procesarBusqueda(data.jobId)

        } catch (err) {
            notificar({
                icon: "error",
                title: "Error",
                text: err.response?.data?.error || err.message
            })
        } finally {
            onJob(null)
            loading.value = false
        }
    }

    // Retoma una búsqueda cuyo job quedó corriendo (ej: tras recargar la página)
    async function reanudarBusqueda(jobId) {
        loading.value = true

        try {
            return await procesarBusqueda(jobId)
        } catch (err) {
            notificar({
                icon: "error",
                title: "Error",
                text: err.message
            })
        } finally {
            onJob(null)
            loading.value = false
        }
    }

    async function procesarCotizacion(jobId) {

        onJob({ tipo: "cotizar", jobId })

        const res = await esperarJob(jobId)

        notificar({
            icon: "success",
            title: "Cotización generada"
        })

        // garantiza retorno truthy cuando el job terminó bien
        return res || true
    }

    // =========================
    // 💰 GENERAR COTIZACIÓN
    // =========================
    async function generarCotizacion({
        precio,
        vuelos = null,
        searchId: customSearchId,
        valorRecobro
    }) {

        const { user } = useAuth()

        const finalSearchId = customSearchId || searchId.value

        if (!finalSearchId) {
            return notificar({
                icon: "warning",
                title: "Falta searchId"
            })
        }

        if (!user.value) {
            return notificar({
                icon: "error",
                title: "Usuario no cargado"
            })
        }

        loading.value = true

        try {

            const { data } = await api.post("/generar-cotizacion", {
                searchId: finalSearchId,
                precio,
                vuelos,
                valorRecobro: valorRecobro.value
            })

            if (!data.ok) {
                throw new Error(data.error || "Error generando cotización")
            }

            return await procesarCotizacion(data.jobId)

        } catch (err) {

            notificar({
                icon: "error",
                title: "Error",
                text: err.response?.data?.error || err.message
            })

        } finally {

            onJob(null)
            loading.value = false

        }
    }

    // Retoma una cotización cuyo job quedó corriendo (ej: tras recargar la página)
    async function reanudarCotizacion(jobId) {
        loading.value = true

        try {
            return await procesarCotizacion(jobId)
        } catch (err) {
            notificar({
                icon: "error",
                title: "Error",
                text: err.message
            })
        } finally {
            onJob(null)
            loading.value = false
        }
    }

    async function generarCotizacionCorp({
        precio,
        vuelos = null,
        searchId: customSearchId,
        porcentajes,
        precioTraslados,
        nombre = null,
        cedula = null
    }) {

        const { user } = useAuth()

        const finalSearchId = customSearchId || searchId.value

        if (!finalSearchId) {
            return notificar({
                icon: "warning",
                title: "Falta searchId"
            })
        }

        if (!user.value) {
            return notificar({
                icon: "error",
                title: "Usuario no cargado"
            })
        }

        loading.value = true

        try {

            const { data } = await api.post("/generar-cotizacioncorp", {
                searchId: finalSearchId,
                precio,
                vuelos,
                porcentajes,
                precioTraslados,
                nombre,
                cedula
            })

            if (!data.ok) {
                throw new Error(data.error || "Error generando cotización")
            }

            return await procesarCotizacion(data.jobId)

        } catch (err) {

            notificar({
                icon: "error",
                title: "Error",
                text: err.response?.data?.error || err.message
            })

        } finally {

            onJob(null)
            loading.value = false

        }
    }

    // =========================
    // 🧹 LIMPIAR
    // =========================

    function resetBusqueda() {
        resultados.value = {
            vuelos: [],
            precio: null,
            resumen: "",
        }

        searchId.value = null
    }

    return {
        resultados,
        loading,
        searchId,
        buscarVuelos,
        reanudarBusqueda,
        generarCotizacion,
        reanudarCotizacion,
        generarCotizacionCorp,
        resetBusqueda
    }
}
